import React, { useEffect, useRef, useCallback } from 'react';
import * as AdaptiveCards from 'adaptivecards';
import { makeStyles, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  cardContainer: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow4,
    overflow: 'hidden',
    maxWidth: '400px',
    '& .ac-container': {
      padding: tokens.spacingHorizontalM,
    },
    '& .ac-textBlock': {
      fontFamily: tokens.fontFamilyBase,
    },
    '& .ac-pushButton': {
      fontFamily: tokens.fontFamilyBase,
      borderRadius: tokens.borderRadiusMedium,
      padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
      cursor: 'pointer',
      border: 'none',
      minWidth: '80px',
    },
    '& .ac-pushButton.style-positive': {
      backgroundColor: tokens.colorBrandBackground,
      color: tokens.colorNeutralForegroundOnBrand,
    },
    '& .ac-pushButton.style-positive:hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
    '& .ac-pushButton.style-default': {
      backgroundColor: tokens.colorNeutralBackground3,
      color: tokens.colorNeutralForeground1,
    },
    '& .ac-pushButton.style-default:hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
    '& .ac-image': {
      borderRadius: tokens.borderRadiusSmall,
    },
  },
});

interface AdaptiveCardRendererProps {
  cardJson: string;
  onAction: (action: string, data: any) => void;
}

export const AdaptiveCardRenderer: React.FC<AdaptiveCardRendererProps> = ({
  cardJson,
  onAction,
}) => {
  const styles = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleAction = useCallback(
    (action: AdaptiveCards.Action) => {
      if (action instanceof AdaptiveCards.SubmitAction) {
        const data = action.data as any;
        console.log('Adaptive Card action:', data);
        onAction(data?.action || 'submit', data);
      }
    },
    [onAction]
  );

  useEffect(() => {
    if (!containerRef.current || !cardJson) return;

    try {
      // Parse the card JSON
      const card = new AdaptiveCards.AdaptiveCard();

      // Configure host config for styling
      card.hostConfig = new AdaptiveCards.HostConfig({
        fontFamily: 'Segoe UI, system-ui, sans-serif',
        containerStyles: {
          default: {
            backgroundColor: '#ffffff',
            foregroundColors: {
              default: {
                default: '#242424',
                subtle: '#616161',
              },
              accent: {
                default: '#0078d4',
                subtle: '#0078d4',
              },
            },
          },
        },
        actions: {
          buttonSpacing: 8,
          actionsOrientation: 'horizontal',
          actionAlignment: 'stretch',
        },
      });

      // Set action handler
      card.onExecuteAction = handleAction;

      // Parse and render
      const cardPayload = JSON.parse(cardJson);
      card.parse(cardPayload);

      const renderedCard = card.render();
      
      // Clear previous content and append new card
      containerRef.current.innerHTML = '';
      if (renderedCard) {
        containerRef.current.appendChild(renderedCard);
      }
    } catch (error) {
      console.error('Error rendering Adaptive Card:', error);
      containerRef.current.innerHTML = '<p>Error rendering card</p>';
    }
  }, [cardJson, handleAction]);

  return <div ref={containerRef} className={styles.cardContainer} />;
};
